import React from "react";
import { Link } from "react-router-dom";

import classNames from "classnames";

import Badge from "components/Badge";
import Grid from "components/Grid";
import Loading from "components/Loading";
import Panel from "components/Panel";
import { formatBytes, formatNumber } from "utils/format";
import { useRoutes } from "utils/routes";

import {
  ServerGroupDashboardServerTableQuery,
  useServerGroupDashboardServerTableQuery,
} from "./gql/Query.generated";

type ServerGroupServer =
  ServerGroupDashboardServerTableQuery["getServerGroupServers"][number];

type ServerRowDataType = {
  name: string;
  id: string;
  databaseCount: number;
  replicationLag?: number | "—" | null;
  streamingState?: string | null;
  isPrimary?: boolean;
};

const ServerTable = ({
  clusterId,
  organizationSlug,
}: {
  clusterId: string;
  organizationSlug: string;
}) => {
  const { data, loading, error } = useServerGroupDashboardServerTableQuery({
    variables: {
      clusterId,
      organizationSlug,
    },
  });
  if (loading || error) {
    <Loading error={!!error} />;
  }
  const servers = data?.getServerGroupServers ?? [];
  return <TableContent data={servers} />;
};

const TableContent = ({ data }: { data: ServerGroupServer[] }) => {
  const servers: ServerRowDataType[] = data.map((server: ServerGroupServer) => {
    const isPrimary = server.latestStats?.isPrimary;
    return {
      name: server.name,
      id: server.humanId,
      databaseCount: server.databaseCount,
      isPrimary,
      replicationLag: isPrimary ? "—" : server.latestStats?.standbyApplyByteLag,
      streamingState: isPrimary
        ? "Running"
        : (server.latestStats?.standbyIsStreaming && "Streaming") || null,
    };
  });

  return (
    <Panel title="All Servers">
      <Grid
        striped
        data={servers}
        defaultSortBy="name"
        columns={[
          {
            field: "name",
            header: "Server",
            width: "minmax(30%,1fr)",
            renderer: function ServerGroupNameCell({
              rowData,
            }: {
              rowData: ServerRowDataType;
            }) {
              const { server } = useRoutes();
              return (
                <span className="flex items-center">
                  <Link to={server(rowData.id)}>{rowData.name}</Link>
                  {rowData.isPrimary != null && (
                    <Badge
                      className={classNames(
                        "ml-[6px] border font-medium",
                        !rowData.isPrimary &&
                          "!bg-[#f0f0f0] !text-[#555] !border-[#ddd]",
                      )}
                    >
                      {rowData.isPrimary ? "primary" : "replica"}
                    </Badge>
                  )}
                </span>
              );
            },
          },
          {
            field: "streamingState",
            header: "State",
            width: "140px",
            nullValue: "n/a",
          },
          {
            field: "replicationLag",
            header: "Replication Lag",
            width: "140px",
            style: "number",
            defaultSortOrder: "DESC",
            nullValue: "n/a",
            renderer: function replicationLag({ fieldData }) {
              return fieldData === "—" ? fieldData : formatBytes(fieldData);
            },
          },
          {
            field: "databaseCount",
            header: "# of Databases",
            width: "140px",
            style: "number",
            defaultSortOrder: "DESC",
            nullValue: "n/a",
            renderer: function serverCount({ fieldData }) {
              return formatNumber(fieldData);
            },
          },
        ]}
      />
    </Panel>
  );
};
export default ServerTable;
